/**
 * Reine Hilfsfunktionen fuer die Experimente auf der LP. Kein window, kein
 * React, kein Netz, damit scripts/check-bucketing.js sie direkt in Node prueft.
 * Alles mit Browser-Zugriff liegt in flags-store.js, visitor-id.js und
 * track-exposure.js.
 */
import { evaluateFlag, STATUS, variantKeys } from './bucketing.js'

export const SURFACE_LP = 'lp'
export const VISITOR_PARAM = 'vid'
export const LP_PARAM_PREFIX = 'exp_'
export const QA_PARAM_PREFIX = 'ff_'
export const CONSENT_CATEGORY = 'performance'

// Dasselbe Muster prueft der Funnel (visitor-id.js im Dashboard), bevor er
// ?vid= uebernimmt.
const VISITOR_ID_PATTERN = /^[a-zA-Z0-9-]{8,64}$/

const STAGES = ['production', 'staging']

/** GATSBY_ENV (= AWS_BRANCH) auf die Stages der Flags abbilden. */
export const stageFrom = (buildEnv) =>
  STAGES.includes(buildEnv) ? buildEnv : 'develop'

export const isValidVisitorId = (id) =>
  typeof id === 'string' && VISITOR_ID_PATTERN.test(id)

/** UUID v4 aus 16 Zufallsbytes, fuer Browser ohne crypto.randomUUID. */
export const uuidFromBytes = (bytes) => {
  const b = Array.from(bytes).slice(0, 16)
  b[6] = (b[6] & 0x0f) | 0x40
  b[8] = (b[8] & 0x3f) | 0x80
  const hex = b.map((n) => n.toString(16).padStart(2, '0')).join('')
  return [
    hex.slice(0, 8),
    hex.slice(8, 12),
    hex.slice(12, 16),
    hex.slice(16, 20),
    hex.slice(20, 32)
  ].join('-')
}

/**
 * System.data ist AWSJSON und kommt als String `{ "flags": {...} }`, je nach
 * Schreibweg auch doppelt kodiert. Alles Unlesbare ergibt keine Flags.
 */
export const parseFlagsData = (raw) => {
  let data = raw
  try {
    for (let i = 0; i < 2 && typeof data === 'string'; i += 1) {
      data = JSON.parse(data)
    }
  } catch {
    return {}
  }

  const flags = data && typeof data === 'object' ? data.flags : null
  if (!flags || typeof flags !== 'object') return {}

  const normalized = {}
  Object.entries(flags).forEach(([key, flag]) => {
    if (flag && typeof flag === 'object') normalized[key] = { ...flag, key }
  })
  return normalized
}

/**
 * ?ff_<key>=<variant> in die gespeicherten QA-Overrides mischen. Ein leerer
 * Wert (?ff_<key>=) loescht den Override wieder.
 */
export const mergeQaOverrides = (search, stored) => {
  const overrides =
    stored && typeof stored === 'object' && !Array.isArray(stored)
      ? { ...stored }
      : {}
  let changed = false

  new URLSearchParams(search || '').forEach((value, name) => {
    if (!name.startsWith(QA_PARAM_PREFIX)) return
    const key = name.slice(QA_PARAM_PREFIX.length)
    if (!key) return
    if (value) overrides[key] = value
    else delete overrides[key]
    changed = true
  })

  return { overrides, changed }
}

/**
 * Wie evaluateFlag, nur mit QA-Override davor. Ein QA-Override zaehlt nie als
 * Exposure.
 */
export const resolveFlag = (flags, key, ctx = {}, qa = {}) => {
  const flag = flags?.[key]
  if (!flag) return evaluateFlag(undefined)

  const forced = qa?.[key]
  if (forced && variantKeys(flag).includes(forced)) {
    return { variant: forced, tracked: false, reason: 'qa' }
  }

  return evaluateFlag(flag, ctx)
}

/** Die LP schreibt nur Exposures fuer Flags, die sie selbst zuteilt. */
export const shouldTrackExposure = (flag, result) =>
  result?.tracked === true && flag?.surface === SURFACE_LP

/**
 * Zuteilungen, die der Funnel als exp_<key> von der LP uebernimmt: jedes
 * laufende Flag mit surface 'lp', egal ob Experiment oder Rollout.
 */
export const lpAssignments = (flags, ctx = {}, qa = {}) => {
  const result = {}
  Object.values(flags || {}).forEach((flag) => {
    if (flag.surface !== SURFACE_LP || flag.status !== STATUS.RUNNING) return
    const { variant } = resolveFlag(flags, flag.key, ctx, qa)
    if (variant) result[flag.key] = variant
  })
  return result
}

/**
 * Haengt vid, exp_* und die QA-Overrides an die Parameter der Funnel-iframe-URL.
 * Alle anderen Parameter bleiben, wie sie sind.
 *
 * vid und exp_* vergibt nur die LP. Stehen sie schon in der LP-URL (z. B.
 * ein weitergeleiteter Link), sind sie fremd und werden ersetzt bzw. entfernt,
 * sonst uebernimmt der Funnel eine Gruppe, die diese LP nie zugeteilt hat.
 * ff_* aus der URL bleiben unangetastet, gespeicherte QA-Overrides kommen nur
 * dazu, wenn der Parameter fehlt.
 */
export const applyFunnelParams = (
  params,
  { visitorId, assignments = {}, qa = {} } = {}
) => {
  new Set(params.keys()).forEach((name) => {
    if (name === VISITOR_PARAM || name.startsWith(LP_PARAM_PREFIX)) {
      params.delete(name)
    }
  })

  if (visitorId) params.set(VISITOR_PARAM, visitorId)

  Object.entries(assignments).forEach(([key, variant]) => {
    params.set(`${LP_PARAM_PREFIX}${key}`, variant)
  })

  Object.entries(qa).forEach(([key, variant]) => {
    const name = `${QA_PARAM_PREFIX}${key}`
    if (variant && !params.has(name)) params.set(name, variant)
  })

  return params
}

/** UTM und gclid aus dem Query-String der Einstiegs-URL. */
export const refFromSearch = (search) => {
  const params = new URLSearchParams(search || '')
  return {
    utm_source: params.get('utm_source') || null,
    utm_campaign: params.get('utm_campaign') || null,
    has_gclid: params.has('gclid')
  }
}

/** Input fuer createExperimentExposure, Felder laut Spezifikation. */
export const buildExposureInput = ({
  key,
  variant,
  visitorId,
  consent,
  ref
}) => ({
  experiment_key: key,
  variant,
  unit: 'visitor',
  unit_id: visitorId,
  visitor_id: visitorId,
  surface: SURFACE_LP,
  utm_source: ref?.utm_source || null,
  utm_campaign: ref?.utm_campaign || null,
  has_gclid: !!ref?.has_gclid,
  consent: !!consent
})

const toCategoryList = (categories) => {
  if (Array.isArray(categories)) return categories
  if (typeof categories !== 'string' || !categories) return []
  try {
    const parsed = JSON.parse(categories)
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return categories.split(',').map((c) => c.trim())
  }
}

/**
 * Consent fuer `performance` aus einem CookieScript-State
 * ({ action, categories }), wie ihn currentState() und das Cookie
 * CookieScriptConsent liefern. Im Cookie ist categories ein JSON-String.
 *
 * Bewusst strenger als hasAnalyticsConsent in gatsby-browser.js: `accept` ohne
 * Kategorien zaehlt hier nicht als Consent, weil davon abhaengt, ob etwas auf
 * dem Geraet gespeichert wird. „Alle akzeptieren" kommt ueber das Event
 * CookieScriptAcceptAll bzw. mit allen Kategorien im State.
 */
export const hasPerformanceConsentIn = (state) => {
  if (!state || state.action !== 'accept') return false
  return toCategoryList(state.categories).includes(CONSENT_CATEGORY)
}

const safeDecode = (value) => {
  try {
    return decodeURIComponent(value)
  } catch {
    return value
  }
}

export const parseConsentCookie = (raw) => {
  if (!raw) return null
  for (const candidate of [raw, safeDecode(raw)]) {
    try {
      const parsed = JSON.parse(candidate)
      if (parsed && typeof parsed === 'object') return parsed
    } catch {
      // naechste Schreibweise probieren
    }
  }
  return null
}
